import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import {
  Plus, Package, FlaskConical, Warehouse, Truck as TruckIcon, X,
  CheckCircle2, AlertTriangle, Clock, Ban, TestTube2, Printer,
} from "lucide-react";

const VENDOR_TYPES = [
  { value: "omc",        label: "OMC (IOCL/BPCL/HPCL)", hint: "Government oil marketing company" },
  { value: "petrolpump", label: "Petrol Pump",           hint: "Retail outlet / filling station" },
  { value: "private",    label: "Private Supplier",      hint: "Private bulk trader" },
];

const OMC_VENDORS = ["IOCL", "BPCL", "HPCL", "Reliance", "Nayara"];

const QUALITY_HINT = {
  density: { min: 0.820, max: 0.845, unit: "kg/L", label: "Density" },
  sulphur_ppm: { min: 0, max: 10, unit: "ppm", label: "Sulphur (BS-VI)" },
  viscosity_cst: { min: 2.0, max: 4.5, unit: "cSt @40°C", label: "Kinematic viscosity" },
};

/** Pill badge for the PO's quality state — colour-codes at a glance. */
function QualityPill({ status }) {
  const cfg = {
    not_tested: { label: "Not tested",     cls: "bg-slate-100 text-slate-600 border-slate-300",  icon: Clock },
    pending:    { label: "Awaiting QT",    cls: "bg-amber-100 text-amber-800 border-amber-300",  icon: TestTube2 },
    passed:     { label: "Passed",          cls: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
    failed:     { label: "FAILED — reject", cls: "bg-red-100 text-red-700 border-red-300",         icon: Ban },
  }[status || "not_tested"];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${cfg.cls}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
}

/** Pill badge for the PO lifecycle status. */
function LifecyclePill({ status }) {
  const cfg = {
    ordered:  { label: "Ordered",         cls: "bg-blue-100 text-blue-700 border-blue-300" },
    received: { label: "In Quarantine",   cls: "bg-amber-100 text-amber-800 border-amber-300" },
    released: { label: "Released",         cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
    cancelled:{ label: "Cancelled",        cls: "bg-slate-100 text-slate-500 border-slate-300" },
  }[status] || { label: status, cls: "bg-slate-100 text-slate-500 border-slate-300" };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export default function Procurement() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["pos"],
    queryFn: () => api.get("/procurement/purchase-orders").then((r) => r.data),
  });
  const { data: tanks } = useQuery({
    queryKey: ["tanks-proc"],
    queryFn: () => api.get("/inventory/tanks").then((r) => r.data),
  });
  const { data: bowsers } = useQuery({
    queryKey: ["bowsers-proc"],
    queryFn: () => api.get("/fleet/bowsers").then((r) => r.data),
  });
  const { data: vendors } = useQuery({
    queryKey: ["vendors-proc"],
    queryFn: () => api.get("/vendors?active_only=true").then((r) => r.data),
  });

  // ─────────── New PO ───────────
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    vendor: "IOCL",
    vendor_type: "omc",
    tank_id: "",
    quantity_litres: 20000,
    rate: 88,
    order_date: new Date().toISOString().slice(0, 10),
    expected_date: "",
  });

  const create = useMutation({
    mutationFn: (p) => api.post("/procurement/purchase-orders", p),
    onSuccess: () => { toast.success("PO created"); setOpen(false); qc.invalidateQueries({ queryKey: ["pos"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  // ─────────── GRN modal ───────────
  const [grnPo, setGrnPo] = useState(null);
  const [grnForm, setGrnForm] = useState({ received_qty: 0, grn_no: "", batch: "", density: 0.832, temperature: 28.5 });
  const doGrn = useMutation({
    mutationFn: ({ id, payload }) => api.post(`/procurement/purchase-orders/${id}/grn`, payload),
    onSuccess: () => {
      toast.success("GRN posted — batch is now in quarantine, awaiting quality test");
      setGrnPo(null);
      qc.invalidateQueries({ queryKey: ["pos"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "GRN failed"),
  });

  // ─────────── Quality Test modal ───────────
  const [qtPo, setQtPo] = useState(null);
  const [qtForm, setQtForm] = useState({
    density: 0.832, temperature: 28.5,
    water_test_pass: true, appearance_ok: true,
    sulphur_ppm: 8, viscosity_cst: 3.2,
    test_type: "inhouse", lab_name: "", remarks: "",
  });
  const doQt = useMutation({
    mutationFn: ({ id, payload }) => api.post(`/procurement/purchase-orders/${id}/quality-test`, payload),
    onSuccess: (r) => {
      const verdict = r.data?.quality_test?.pass_fail;
      if (verdict === "PASS") toast.success("Quality passed — batch is ready to release.");
      else toast.error(`Quality FAILED — ${(r.data?.quality_test?.fail_reasons || []).join("; ") || "check parameters"}`);
      setQtPo(null);
      qc.invalidateQueries({ queryKey: ["pos"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Quality test failed"),
  });

  // ─────────── Release modal ───────────
  const [relPo, setRelPo] = useState(null);
  const [relTargets, setRelTargets] = useState([{ kind: "tank", id: "", qty: 0 }]);
  const doRelease = useMutation({
    mutationFn: ({ id, payload }) => api.post(`/procurement/purchase-orders/${id}/release`, payload),
    onSuccess: () => {
      toast.success("Batch released to storage");
      setRelPo(null);
      qc.invalidateQueries({ queryKey: ["pos"] });
      qc.invalidateQueries({ queryKey: ["tanks-proc"] });
      qc.invalidateQueries({ queryKey: ["bowsers-proc"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Release failed"),
  });

  const openReleaseFor = (po) => {
    setRelPo(po);
    const remaining = Math.max(0, (po.received_qty || 0) - (po.released_qty || 0));
    setRelTargets([{ kind: "tank", id: po.tank_id || "", qty: remaining }]);
  };

  const relRemaining = useMemo(() => {
    if (!relPo) return 0;
    return Math.max(0, (relPo.received_qty || 0) - (relPo.released_qty || 0));
  }, [relPo]);
  const relRequested = useMemo(
    () => relTargets.reduce((s, t) => s + (Number(t.qty) || 0), 0),
    [relTargets],
  );
  const relOverflow = relRequested > relRemaining + 1e-6;

  const rows = data?.items || [];

  return (
    <div className="space-y-4" data-testid="procurement-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Procurement</h1>
          <p className="text-sm text-muted-foreground">
            Purchase orders → GRN (quarantine) → Quality Test → Release to tanks / bowsers.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-po"><Plus size={14} className="mr-1" /> New PO</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>New purchase order</DialogTitle>
              <DialogDescription>Source fuel from an OMC, another petrol pump, or a private supplier.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>Vendor type</Label>
                <Select value={form.vendor_type} onValueChange={(v) => setForm((f) => ({ ...f, vendor_type: v }))}>
                  <SelectTrigger data-testid="po-vendor-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VENDOR_TYPES.map((v) => (
                      <SelectItem key={v.value} value={v.value}>
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold">{v.label}</span>
                          <span className="text-[10px] text-muted-foreground">{v.hint}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Vendor</Label>
                {form.vendor_type === "omc" ? (
                  <Select value={form.vendor} onValueChange={(v) => setForm((f) => ({ ...f, vendor: v }))}>
                    <SelectTrigger data-testid="po-vendor"><SelectValue /></SelectTrigger>
                    <SelectContent>{OMC_VENDORS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <>
                    <Input
                      list="saved-vendors"
                      value={form.vendor}
                      onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
                      placeholder={form.vendor_type === "petrolpump" ? "e.g. HP Pune Chinchwad" : "e.g. Prakash Fuels Pvt Ltd"}
                      data-testid="po-vendor-freetext"
                    />
                    <datalist id="saved-vendors">
                      {(vendors?.items || [])
                        .filter((v) => v.vendor_type === form.vendor_type)
                        .map((v) => <option key={v.id} value={v.name}>{v.city ? `${v.city}` : ""}</option>)}
                    </datalist>
                    <div className="mt-1 text-[10px] text-muted-foreground">
                      Suggestions come from your <a href="/app/vendors" className="text-primary hover:underline">Vendor Master</a>. You can also type a new name.
                    </div>
                  </>
                )}
              </div>
              <div className="sm:col-span-2">
                <Label>Suggested destination tank (optional)</Label>
                <Select value={form.tank_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, tank_id: v === "__none__" ? "" : v }))}>
                  <SelectTrigger data-testid="po-tank"><SelectValue placeholder="Not decided yet" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Decide at release —</SelectItem>
                    {(tanks?.items || []).map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.code} · {t.name} ({Math.round(t.current_stock || 0)} / {t.capacity_litres} L)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity (L)</Label>
                <Input type="number" value={form.quantity_litres}
                  onChange={(e) => setForm((f) => ({ ...f, quantity_litres: Number(e.target.value) }))}
                  data-testid="po-qty" />
              </div>
              <div>
                <Label>Rate (₹/L)</Label>
                <Input type="number" step="0.1" value={form.rate}
                  onChange={(e) => setForm((f) => ({ ...f, rate: Number(e.target.value) }))} />
              </div>
              <div>
                <Label>Order date</Label>
                <Input type="date" value={form.order_date}
                  onChange={(e) => setForm((f) => ({ ...f, order_date: e.target.value }))} />
              </div>
              <div>
                <Label>Expected date</Label>
                <Input type="date" value={form.expected_date}
                  onChange={(e) => setForm((f) => ({ ...f, expected_date: e.target.value }))} />
              </div>
              <div className="sm:col-span-2 rounded border border-primary/30 bg-primary/5 p-2 text-sm">
                Total value: <b className="text-primary">₹{(form.quantity_litres * form.rate).toLocaleString("en-IN")}</b>
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={() => create.mutate(form)}
                disabled={!form.vendor || create.isPending}
                data-testid="btn-save-po"
              >
                Create PO
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* PO table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Vendor</th>
                  <th className="px-4 py-3 text-right">Qty (L)</th>
                  <th className="px-4 py-3 text-right">Rate</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Quality</th>
                  <th className="px-4 py-3">Progress</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const received = p.received_qty || 0;
                  const released = p.released_qty || 0;
                  const inQuarantine = Math.max(0, received - released);
                  const pct = received > 0 ? Math.round((released / received) * 100) : 0;
                  return (
                    <tr key={p.id} className="border-b border-border/40 text-sm" data-testid={`po-row-${p.code}`}>
                      <td className="px-4 py-3 font-mono font-semibold">{p.code}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{p.vendor}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                          {(VENDOR_TYPES.find((v) => v.value === p.vendor_type) || {}).label || p.vendor_type}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{(p.quantity_litres || 0).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3 text-right font-mono">₹{p.rate}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">₹{(p.total_amount || 0).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3"><LifecyclePill status={p.status} /></td>
                      <td className="px-4 py-3">
                        {p.status === "ordered" ? <span className="text-xs text-muted-foreground">—</span> : <QualityPill status={p.quality_status} />}
                      </td>
                      <td className="px-4 py-3">
                        {p.status === "received" || p.status === "released" ? (
                          <div className="min-w-[110px]">
                            <div className="mb-1 text-[10px] text-muted-foreground">
                              {Math.round(released).toLocaleString("en-IN")} / {Math.round(received).toLocaleString("en-IN")} L released
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-muted">
                              <div className="h-1.5 rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            {inQuarantine > 0 && (
                              <div className="mt-1 text-[10px] font-semibold text-amber-700">
                                {Math.round(inQuarantine).toLocaleString("en-IN")} L in quarantine
                              </div>
                            )}
                          </div>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <POActionButton
                          po={p}
                          onGrn={() => {
                            setGrnPo(p);
                            setGrnForm({
                              received_qty: p.quantity_litres,
                              grn_no: `GRN-${Date.now().toString().slice(-6)}`,
                              batch: `B-${p.vendor.replace(/\s+/g, "").slice(0, 6)}-${Date.now().toString().slice(-4)}`,
                              density: 0.832, temperature: 28.5,
                            });
                          }}
                          onQt={() => {
                            setQtPo(p);
                            setQtForm({
                              density: p.density || 0.832,
                              temperature: p.temperature || 28.5,
                              water_test_pass: true, appearance_ok: true,
                              sulphur_ppm: 8, viscosity_cst: 3.2,
                              test_type: "inhouse", lab_name: "", remarks: "",
                            });
                          }}
                          onRelease={() => openReleaseFor(p)}
                        />
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-muted-foreground">No purchase orders yet — click <b>New PO</b> to start.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ────────── GRN dialog ────────── */}
      <Dialog open={!!grnPo} onOpenChange={(v) => !v && setGrnPo(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="grn-dialog">
          <DialogHeader>
            <DialogTitle>Receive stock (GRN) · {grnPo?.code}</DialogTitle>
            <DialogDescription>
              Record physical receipt from <b>{grnPo?.vendor}</b>. The batch will be parked in <b>quarantine</b> until a quality test passes — it does not enter any tank yet.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Received qty (L)</Label><Input type="number" value={grnForm.received_qty} onChange={(e) => setGrnForm({ ...grnForm, received_qty: Number(e.target.value) })} data-testid="grn-qty" /></div>
            <div><Label>GRN no</Label><Input value={grnForm.grn_no} onChange={(e) => setGrnForm({ ...grnForm, grn_no: e.target.value })} data-testid="grn-no" /></div>
            <div className="sm:col-span-2"><Label>Batch code</Label><Input value={grnForm.batch} onChange={(e) => setGrnForm({ ...grnForm, batch: e.target.value })} data-testid="grn-batch" /></div>
            <div><Label>Density at receipt</Label><Input type="number" step="0.001" value={grnForm.density} onChange={(e) => setGrnForm({ ...grnForm, density: Number(e.target.value) })} /></div>
            <div><Label>Temp. at receipt (°C)</Label><Input type="number" step="0.1" value={grnForm.temperature} onChange={(e) => setGrnForm({ ...grnForm, temperature: Number(e.target.value) })} /></div>
          </div>
          <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
            <AlertTriangle size={12} className="mr-1 inline" />
            After GRN: batch is <b>quarantined</b>. Run the quality test to unlock release.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrnPo(null)}>Cancel</Button>
            <Button onClick={() => doGrn.mutate({ id: grnPo.id, payload: grnForm })} disabled={doGrn.isPending} data-testid="btn-confirm-grn">
              <Package size={14} className="mr-1" />
              {doGrn.isPending ? "Posting…" : "Post GRN → quarantine"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ────────── Quality Test dialog (Step 1 of wizard) ────────── */}
      <Dialog open={!!qtPo} onOpenChange={(v) => !v && setQtPo(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="qt-dialog">
          <DialogHeader>
            <DialogTitle>Step 1 · Quality Test · {qtPo?.code}</DialogTitle>
            <DialogDescription>
              Batch <b>{qtPo?.batch || "—"}</b> · {(qtPo?.received_qty || 0).toLocaleString("en-IN")} L from <b>{qtPo?.vendor}</b>.
              Values are auto-checked against BS-VI HSD spec. Any parameter out of range → FAIL.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>Density (kg/L)</Label>
              <Input type="number" step="0.001" value={qtForm.density}
                onChange={(e) => setQtForm({ ...qtForm, density: Number(e.target.value) })}
                data-testid="qt-density"
              />
              <SpecHint {...QUALITY_HINT.density} />
            </div>
            <div>
              <Label>Temperature (°C)</Label>
              <Input type="number" step="0.1" value={qtForm.temperature}
                onChange={(e) => setQtForm({ ...qtForm, temperature: Number(e.target.value) })}
              />
              <div className="mt-1 text-[10px] text-muted-foreground">Alert &gt; 45°C — indicates storage issue</div>
            </div>
            <div>
              <Label>Sulphur (ppm)</Label>
              <Input type="number" step="0.1" value={qtForm.sulphur_ppm}
                onChange={(e) => setQtForm({ ...qtForm, sulphur_ppm: Number(e.target.value) })}
                data-testid="qt-sulphur"
              />
              <SpecHint {...QUALITY_HINT.sulphur_ppm} />
            </div>
            <div>
              <Label>Viscosity (cSt @40°C)</Label>
              <Input type="number" step="0.1" value={qtForm.viscosity_cst}
                onChange={(e) => setQtForm({ ...qtForm, viscosity_cst: Number(e.target.value) })}
              />
              <SpecHint {...QUALITY_HINT.viscosity_cst} />
            </div>
            <label className="col-span-1 flex items-center gap-2 rounded-md border border-border/60 p-3">
              <input
                type="checkbox"
                checked={qtForm.water_test_pass}
                onChange={(e) => setQtForm({ ...qtForm, water_test_pass: e.target.checked })}
                className="h-4 w-4"
                data-testid="qt-water"
              />
              <div>
                <div className="text-sm font-semibold">Water test PASS</div>
                <div className="text-[10px] text-muted-foreground">No free water in sample</div>
              </div>
            </label>
            <label className="col-span-1 flex items-center gap-2 rounded-md border border-border/60 p-3">
              <input
                type="checkbox"
                checked={qtForm.appearance_ok}
                onChange={(e) => setQtForm({ ...qtForm, appearance_ok: e.target.checked })}
                className="h-4 w-4"
                data-testid="qt-appearance"
              />
              <div>
                <div className="text-sm font-semibold">Appearance OK</div>
                <div className="text-[10px] text-muted-foreground">Clear &amp; bright, no sediment</div>
              </div>
            </label>
            <div>
              <Label>Test type</Label>
              <Select value={qtForm.test_type} onValueChange={(v) => setQtForm({ ...qtForm, test_type: v })}>
                <SelectTrigger data-testid="qt-test-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inhouse">In-house (depot)</SelectItem>
                  <SelectItem value="lab">External lab</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {qtForm.test_type === "lab" && (
              <div>
                <Label>Lab name</Label>
                <Input value={qtForm.lab_name} onChange={(e) => setQtForm({ ...qtForm, lab_name: e.target.value })} placeholder="SGS India / Intertek / …" />
              </div>
            )}
            <div className="sm:col-span-2">
              <Label>Remarks</Label>
              <Textarea rows={2} value={qtForm.remarks} onChange={(e) => setQtForm({ ...qtForm, remarks: e.target.value })} placeholder="Anything else the operator should know…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQtPo(null)}>Cancel</Button>
            <Button onClick={() => doQt.mutate({ id: qtPo.id, payload: qtForm })} disabled={doQt.isPending} data-testid="btn-confirm-qt">
              <FlaskConical size={14} className="mr-1" />
              {doQt.isPending ? "Testing…" : "Run test & record result"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ────────── Release dialog (Step 2 of wizard) ────────── */}
      <Dialog open={!!relPo} onOpenChange={(v) => !v && setRelPo(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="release-dialog">
          <DialogHeader>
            <DialogTitle>Step 2 · Release from Quarantine · {relPo?.code}</DialogTitle>
            <DialogDescription>
              <span className="mr-3">Batch <b>{relPo?.batch || "—"}</b></span>
              <span className="mr-3">Vendor <b>{relPo?.vendor}</b></span>
              <span>Available: <b>{Math.round(relRemaining).toLocaleString("en-IN")} L</b></span>
            </DialogDescription>
          </DialogHeader>

          <div className="rounded border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-800">
            <CheckCircle2 size={12} className="mr-1 inline" />
            Quality passed. Split this batch across any number of tanks and bowsers below.
          </div>

          <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
            {relTargets.map((t, i) => (
              <div key={i} className="grid grid-cols-2 items-end gap-2 rounded-md border border-border/60 p-2 sm:grid-cols-[110px_1fr_120px_auto]">
                <div>
                  <Label className="text-[10px]">Kind</Label>
                  <Select value={t.kind} onValueChange={(v) => setRelTargets((rows) => rows.map((r, ix) => ix === i ? { ...r, kind: v, id: "" } : r))}>
                    <SelectTrigger data-testid={`rel-kind-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tank"><Warehouse size={12} className="mr-1 inline" /> Tank</SelectItem>
                      <SelectItem value="bowser"><TruckIcon size={12} className="mr-1 inline" /> Bowser</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-[10px]">Destination</Label>
                  <Select value={t.id} onValueChange={(v) => setRelTargets((rows) => rows.map((r, ix) => ix === i ? { ...r, id: v } : r))}>
                    <SelectTrigger data-testid={`rel-dest-${i}`}><SelectValue placeholder={t.kind === "tank" ? "Pick tank" : "Pick bowser"} /></SelectTrigger>
                    <SelectContent>
                      {t.kind === "tank"
                        ? (tanks?.items || []).map((tk) => (
                            <SelectItem key={tk.id} value={tk.id}>
                              {tk.code} · {tk.name} — free {Math.max(0, (tk.capacity_litres || 0) - (tk.current_stock || 0)).toLocaleString("en-IN")} L
                            </SelectItem>
                          ))
                        : (bowsers?.items || []).map((b) => (
                            <SelectItem key={b.id} value={b.id}>
                              {b.code} · {b.registration_no} — free {Math.max(0, (b.capacity_litres || 0) - (b.current_stock || 0)).toLocaleString("en-IN")} L
                            </SelectItem>
                          ))
                      }
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Quantity (L)</Label>
                  <Input
                    type="number"
                    value={t.qty}
                    onChange={(e) => setRelTargets((rows) => rows.map((r, ix) => ix === i ? { ...r, qty: Number(e.target.value) } : r))}
                    data-testid={`rel-qty-${i}`}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setRelTargets((rows) => rows.filter((_, ix) => ix !== i))}
                  disabled={relTargets.length === 1}
                  data-testid={`rel-remove-${i}`}
                  className="justify-self-end"
                >
                  <X size={14} />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRelTargets((rows) => [...rows, { kind: "tank", id: "", qty: 0 }])}
              data-testid="rel-add-target"
            >
              <Plus size={12} className="mr-1" /> Add destination
            </Button>
          </div>

          <div className="rounded-md border border-border/60 p-3 text-sm">
            <div className="flex justify-between">
              <span>Requested total</span>
              <span className={`font-mono font-bold ${relOverflow ? "text-red-600" : ""}`}>
                {relRequested.toLocaleString("en-IN")} L
              </span>
            </div>
            <div className="flex justify-between">
              <span>Available in quarantine</span>
              <span className="font-mono">{relRemaining.toLocaleString("en-IN")} L</span>
            </div>
            {relOverflow && (
              <div className="mt-1 text-xs text-red-600">Requested amount exceeds quarantine remaining.</div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRelPo(null)}>Cancel</Button>
            <Button
              onClick={() => doRelease.mutate({
                id: relPo.id,
                payload: { targets: relTargets.filter((t) => t.id && t.qty > 0) },
              })}
              disabled={
                doRelease.isPending || relOverflow ||
                relTargets.some((t) => !t.id || !(t.qty > 0))
              }
              data-testid="btn-confirm-release"
            >
              <Warehouse size={14} className="mr-1" />
              {doRelease.isPending ? "Releasing…" : `Release ${relRequested.toLocaleString("en-IN")} L`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Action buttons that reflect where the PO is in the lifecycle. Print is
 * available at every stage after GRN so operators can label the physical batch. */
function POActionButton({ po, onGrn, onQt, onRelease }) {
  const canPrint = po.status === "received" || po.status === "released";
  const printBtn = canPrint ? (
    <Button
      size="sm"
      variant="outline"
      onClick={() => {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        window.open(`${base}/api/procurement/purchase-orders/${po.id}/batch-label`, "_blank", "noopener");
      }}
      data-testid={`btn-print-${po.code}`}
      title="Print batch label"
    >
      <Printer size={12} />
    </Button>
  ) : null;

  let primary;
  if (po.status === "ordered") {
    primary = <Button size="sm" variant="outline" onClick={onGrn} data-testid={`btn-grn-${po.code}`}><Package size={12} className="mr-1" /> Receive</Button>;
  } else if (po.status === "cancelled") {
    primary = <span className="text-xs text-muted-foreground">Cancelled</span>;
  } else if (po.status === "released") {
    primary = <span className="text-xs font-semibold text-emerald-700">✓ Fully released</span>;
  } else if (po.quality_status === "pending" || po.quality_status === "not_tested") {
    primary = <Button size="sm" onClick={onQt} data-testid={`btn-qt-${po.code}`}><FlaskConical size={12} className="mr-1" /> Run QT</Button>;
  } else if (po.quality_status === "failed") {
    primary = (
      <Button size="sm" variant="outline" onClick={onQt} data-testid={`btn-qt-retry-${po.code}`}>
        <FlaskConical size={12} className="mr-1" /> Re-test
      </Button>
    );
  } else {
    primary = <Button size="sm" onClick={onRelease} data-testid={`btn-release-${po.code}`}><Warehouse size={12} className="mr-1" /> Release</Button>;
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {primary}
      {printBtn}
    </div>
  );
}

function SpecHint({ min, max, unit, label }) {
  return (
    <div className="mt-1 text-[10px] text-muted-foreground">
      {label}: {min}–{max} {unit}
    </div>
  );
}
